import { AddCircle, Clear, Delete, Download, Search } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import AutoComplete from "@/components/hookForms/autoComplete";
import ConfirmationDialog from "../../../components/confirmationDialog";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import IGenericResult from "@/interfaces/IGenericResult";
import TextField from "@/components/hookForms/textField";
import messages from "@/utils/messages";
import { s3download } from "@/utils/s3";
import serviceRelatorio from "@/services/serviceRelatorio";
import { toast } from "react-toastify";
import { useAuth } from "@/context/global";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

const Listagemrelatorios = () => {
    const { setTitlePage } = useAuth();

    useEffect(() => {
        setTitlePage("Relatórios - Lista de relatórios");
    }, [setTitlePage]);

    const { handleGetDataUser } = useAuth();
    const user = handleGetDataUser();

    const [isLoading, setLoading] = useState(false);
    const [filters, setFilters] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [data, setData] = useState<any[]>([]);
    const history = useHistory();
    const [tipoRelatorioDataOptions, setTipoRelatorioDataOptions] = useState<IGenericResult[]>([]);
    const [exclusionState, setExclusionState] = useState({ data: "", open: false });

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            status: "",
            usuario: "",
            tipo: "",
            relatorioTipoID: "",
            dataRequisicaoInicial: "",
            dataRequisicaoFinal: "",
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                minWidth: 220,
                flex: 1,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Download">
                                <Download />
                            </Tooltip>
                        }
                        label="Download"
                        onClick={() => {
                            const bucket = process.env.REACT_APP_S3_BUCKET_RELATORIOS_REQUISITADOS || "";
                            s3download(bucket, `Relatorio_${params.id.toString()}.csv`);
                        }}
                        disabled={
                            params.row.status === "E" ||
                            //Usuários que não são admin/master/regular só podem baixar
                            //relatórios gerados por ele mesmo
                            (user &&
                                user?.perfil &&
                                !"admin,master,regular".includes(user?.perfil.toLowerCase()) &&
                                user?.nome &&
                                params.row.usuarioNome !== user.nome)
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Excluir">
                                <Delete />
                            </Tooltip>
                        }
                        label="Excluir"
                        onClick={() => setExclusionState({ data: params.id.toString(), open: true })}
                        disabled={
                            //Usuários que não são admin/master/regular só podem excluir
                            //relatórios gerados por ele mesmo
                            user &&
                            user?.perfil &&
                            !"admin,master,regular".includes(user?.perfil.toLowerCase()) &&
                            user?.nome &&
                            params.row.usuarioNome !== user.nome
                        }
                    />,
                ],
            },
        ],
        [user]
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "relatorioRequisicaoID",
                type: "number",
                headerAlign: "left",
                identity: true,
                width: 150,
            },
            {
                headerName: "Tipo",
                field: "relatorioTipoDescricao",
                type: "string",
                width: 250,
            },
            {
                headerName: "Usuário",
                field: "usuarioNome",
                type: "string",
                width: 250,
            },
            {
                headerName: "Status",
                field: "status",
                type: "string",
                width: 180,
                valueFormatter: (item: any) => {
                    if (item.value === "G") return "Gerado";
                    else if (item.value === "P") return "Pendente";
                    else if (item.value === "E") return "Erro";
                },
            },
            {
                headerName: "Data Geração",
                field: "dataGeracao",
                type: "string",
                width: 180,
                valueGetter: (params: { row: GridRowModel }) =>
                    params.row.dataGeracao ? parseISO(params.row.dataGeracao) : "",
                valueFormatter: (item: any) => {
                    return item.value ? format(item.value, "dd/MM/yyyy HH:mm:ss") : "";
                },
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        serviceRelatorio
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                setData(result?.data.content);
                setRowCount(result?.data.numberOfElements);
            })
            .catch((err) => {
                toast.error(messages.getWithFilterError, err);
                setData([]);
                setRowCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        setTipoRelatorioData();
    }, []);

    const setTipoRelatorioData = async () => {
        serviceRelatorio
            .getAllRelatorioTipo()
            .then((result) => {
                setTipoRelatorioDataOptions(result?.data);
            })
            .catch(() => {
                setTipoRelatorioDataOptions([]);
            });
    };

    useEffect(
        () => {
            getAllWithFilters(filters);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, sortModel]
    );

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
            status: values.status === "Todos" ? "" : values.status,
        };
        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    const onClear = () => {
        reset();
        setFilters(null);
        getAllWithFilters(null);
    };

    const handleDelete = async () => {
        serviceRelatorio
            .delete(exclusionState.data)
            .then((result) => {
                if (result) {
                    toast.success(messages.deleteSucess);
                }
            })
            .catch((err) => {
                toast.error(messages.deleteError, err);
            })
            .finally(() => {
                setExclusionState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item md={4} xs={12}>
                                    <TextField name="usuario" label="Usuário" control={control} error={errors} />
                                </Grid>
                                <Grid item md={8} xs={12}>
                                    <Grid container spacing={2} display="flex" alignItems="center">
                                        <Grid item md={6} xs={12}>
                                            <AutoComplete
                                                name="status"
                                                label="Status"
                                                control={control}
                                                error={errors}
                                                options={[
                                                    { label: "Todos", value: "Todos" },
                                                    { label: "Pendente", value: "P" },
                                                    { label: "Gerado", value: "G" },
                                                    { label: "Erro", value: "E" },
                                                ]}
                                            />
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                                disabled={isLoading}
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item md={3} xs={12}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<AddCircle />}
                                                onClick={() => {
                                                    history.push("/relatorios/gerarrelatorios");
                                                }}
                                                disableElevation
                                                fullWidth
                                                size="medium"
                                            >
                                                Novo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item md={8}>
                                    <AutoComplete
                                        name="relatorioTipoID"
                                        label="Tipo"
                                        control={control}
                                        error={errors}
                                        options={tipoRelatorioDataOptions}
                                        concatWithID={false}
                                    />
                                </Grid>
                                <Grid item md={2}>
                                    <Button
                                        disableElevation
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        style={{ margin: "16px 0px 8px 0px" }}
                                        startIcon={<Clear />}
                                        onClick={() => {
                                            onClear();
                                        }}
                                    >
                                        Limpar
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.relatorioRequisicaoID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>
                </CardContent>

                <ConfirmationDialog
                    open={exclusionState?.open}
                    title={"Excluir"}
                    message={messages.deleteConfirmation}
                    cancelButtonText={"Não"}
                    confirmButtonText={"Sim"}
                    onClose={() => setExclusionState({ data: "", open: false })}
                    onCancel={() => setExclusionState({ data: "", open: false })}
                    onConfirm={() => {
                        setExclusionState({ data: "", open: false });
                        handleDelete();
                    }}
                />
            </Card>
        </Container>
    );
};

export default Listagemrelatorios;
